import { TRAITS, MAXES, QUESTIONS, ROLES } from "@/data/quizData";

// ---------- scoring ----------
const mean = (v) => v.reduce((a, b) => a + b, 0) / v.length;
const centre = (v) => {
  const m = mean(v);
  return v.map((x) => x - m);
};
const length = (v) => Math.sqrt(v.reduce((a, b) => a + b * b, 0));
const dot = (a, b) => a.reduce((s, x, i) => s + x * b[i], 0);

// Each role's profile is turned into a unit-length "shape" once.
const PROFILES = ROLES.map((r) => {
  const c = centre(TRAITS.map((t) => (r.scores[t] || 0) / 5));
  const n = length(c) || 1;
  return c.map((x) => x / n);
});

// choices: one answer number (0 to 3) for each question
export function traitScores(choices) {
  const raw = Object.fromEntries(TRAITS.map((t) => [t, 0]));
  choices.forEach((a, q) => {
    const ans = QUESTIONS[q].answers[a];
    raw[ans.main] += 2;
    raw[ans.minor] += 1;
  });
  return TRAITS.map((t) => raw[t] / MAXES[t]);
}

// All roles, best match first.
export function rankRoles(choices) {
  const u = centre(traitScores(choices));
  const n = length(u) + 1e-9;
  const unit = u.map((x) => x / n);
  return ROLES.map((role, i) => ({ role, score: dot(unit, PROFILES[i]) + role.nudge })).sort(
    (a, b) => b.score - a.score
  );
}

// The winner, plus two runners-up from different shows.
export function quizResult(choices) {
  const ranked = rankRoles(choices);
  const winner = ranked[0].role;
  const runnersUp = [];
  const usedShows = new Set([winner.show]);
  for (const { role } of ranked.slice(1, 40)) {
    if (usedShows.has(role.show)) continue;
    runnersUp.push(role);
    usedShows.add(role.show);
    if (runnersUp.length === 2) break;
  }
  return { winner, runnersUp };
}

// ---------- matching the quiz to the real cast list on the site ----------
export const norm = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const withoutThe = (s) => s.replace(/^the /, "");
const NOT_A_MAIN_ROLE = /\b(alternate|standby|cover|understudy|swing)\b/;

export function findShow(shows, role) {
  const want = norm(role.dbShow);
  return (shows || []).find((s) => norm(s.name) === want) || null;
}

// Who is playing this role today (nobody who has left, or who has not started yet).
export function currentPerformers(show, role) {
  if (!show) return [];
  const today = new Date().toISOString().slice(0, 10);
  const want = withoutThe(norm(role.role));
  const names = [];
  (show.cast || []).forEach((c) => {
    if ((c.group || "Principal") !== "Principal") return;
    if ((c.ends || "").slice(0, 10) && c.ends.slice(0, 10) < today) return;
    if ((c.starts || "").slice(0, 10) && c.starts.slice(0, 10) > today) return;
    const dbRole = withoutThe(norm(c.role));
    if (NOT_A_MAIN_ROLE.test(dbRole)) return;
    const hit = role.exact ? dbRole === want : ` ${dbRole} `.includes(` ${want} `);
    if (hit && c.actor_name && !names.includes(c.actor_name)) names.push(c.actor_name);
  });
  return names;
}